import {apiService, ApiResponse} from 'features/jwt/apiService';
import { apiErrorFrom } from 'features/errors';
import { CookieStore } from 'features/cookies/cookie-store';
import { getCurrentSiteId } from 'features/services/utils/env-helper';
import { ObjectStatus, ResidenceType, SortBy } from 'features/search/filters/enums';
import {
    ApiFilters,
    CategorizedSearch,
    SearchFilters,
    SearchQuery,
    SearchResponse,
    SearchResult,
    Tag,
} from 'features/search/types';
import {
    convertFiltersForApi,
    defaultStageAttributes,
    toObjectSort,
    translateStagePriceOrder,
} from 'features/search/helpers';
import { SearchServiceEndpoints } from './config';
import { User } from 'features/authentication/models/User';

const appendGeoParam = (baseUrl: string, useGeo: boolean): string => {
    if (!useGeo) return baseUrl;
    const uid = CookieStore.getUidCookie();
    if (!uid) return baseUrl;
    return uid.includes('anon-') ? `${baseUrl}?geo=auto:${uid}` : `${baseUrl}?geo=auto`;
};

export class SearchService {
    static async fetchSharedSearchResult(
        filters: SearchFilters,
        sortOrder: string,
        offset: number,
        limit: number,
        useLimitOverride: boolean
    ): Promise<SearchResponse<CategorizedSearch>> {
        const filter = convertFiltersForApi(filters);
        filter.objectStatus = [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.PRESELLING];

        const objectLimit = CookieStore.getSearchResultItems() === null || !useLimitOverride
            ? limit
            : parseInt(CookieStore.getSearchResultItems() as string);

        const [response, objectResponse] = await Promise.all([
            SearchService.fetchStagesAndAreas(filter, sortOrder),
            SearchService.fetchObjects(filter, sortOrder, offset, objectLimit),
        ]);

        // Merge responses
        if (response?.data?.hits) {
            response.data.hits.objects = objectResponse?.data?.hits ?? [];
        }
        if (response?.data?.facetDistribution?.type) {
            response.data.facetDistribution.type.object = objectResponse?.data?.estimatedTotalHits ?? 0;
        }

        return response.data!;
    }

    static async fetchStagesAndAreas(
        filters: ApiFilters,
        sortOrder?: string
    ): Promise<ApiResponse<SearchResponse<CategorizedSearch>>> {
        let order: string[];
        if (sortOrder) {
            order = ['sortOrder:asc', ...translateStagePriceOrder(sortOrder as SortBy)];
        } else {
            order = ['sortOrder:asc', 'inhabitDateTimeStamp:asc'];
        }

        const url = appendGeoParam(SearchServiceEndpoints.searchStageAndArea, !!filters.inhabitDate?.includes('Now'));

        // objectStatus belongs to the object endpoint; strip it so we don't leak it to stage_and_area_search.
        const { objectStatus: _objectStatus, ...stageFilters } = filters;

        return await apiService.post<SearchResponse<CategorizedSearch>>(url, {
            sort: order,
            filter: {
                ...stageFilters,
                areaSiteId: [getCurrentSiteId()],
                areaAttributesToRetrieve: [
                    'type',
                    'name',
                    'url',
                    'searchImage.url',
                    'searchImage.alttext',
                    'searchImage.orgWidth',
                    'searchImage.orgHeight',
                    'area',
                    'municipality',
                    'county',
                ],
            },
            attributesToRetrieve: defaultStageAttributes,
        });
    }

    static async fetchObjects(
        filters: ApiFilters,
        sortOrder: string,
        offset: number,
        limit?: number
    ): Promise<ApiResponse<SearchResponse<SearchResult[]>>> {
        const sortObject = toObjectSort(sortOrder);
        const url = appendGeoParam(SearchServiceEndpoints.searchObject, !!filters.inhabitDate?.includes('Now'));

        const nonParkingResidenceTypes = [
            ResidenceType.APARTMENT,
            ResidenceType.HOUSE,
            ResidenceType.TOWN_HOUSE,
            ResidenceType.DETACHED_HOUSE,
            ResidenceType.SEMI_DETACHED_HOUSE,
            ResidenceType.PLOT,
        ];

        return await apiService.post<SearchResponse<SearchResult[]>>(url, {
            limit: limit,
            offset: offset,
            sort: sortObject,
            filter: {
                ...filters,
                residenceTypes: filters.residenceTypes ?? nonParkingResidenceTypes,
            },
        });
    }

    static async fetchParkingSpaces(
        filters: ApiFilters,
        sortOrder: string,
        offset: number,
        limit?: number
    ): Promise<ApiResponse<SearchResponse<SearchResult[]>>> {
        const sortObject = sortOrder === '' ? ['sortOrder:asc'] : ['sortOrder:asc', sortOrder];

        return await apiService.post<SearchResponse<SearchResult[]>>(SearchServiceEndpoints.searchObject, {
            limit: limit,
            offset: offset,
            sort: sortObject,
            filter: {
                ...filters,
                residenceTypes: [ResidenceType.PARKING_SPACE],
            },
        });
    }

    static async fetchStages(
        searchQuery: SearchQuery<ApiFilters>,
        showCloseBy?: boolean
    ): Promise<ApiResponse<SearchResponse<SearchResult[]>>> {
        const url = appendGeoParam(SearchServiceEndpoints.searchStage, !!showCloseBy);

        return await apiService.post<SearchResponse<SearchResult[]>>(url, searchQuery);
    }

    static async searchTagsByUrls(urls: string[]): Promise<ApiResponse<SearchResponse<Tag[]>>> {
        return await apiService.post<SearchResponse<Tag[]>>(SearchServiceEndpoints.searchTagsByUrl, {
            urls: urls,
        });
    }

    static async searchTagsByString(
        text: string,
        filter: ApiFilters,
        limit = 20,
        sort?: string[]
    ): Promise<ApiResponse<SearchResponse<Tag[]>>> {
        return await apiService.post<SearchResponse<Tag[]>>(SearchServiceEndpoints.searchTags, {
            text: text,
            limit: limit,
            filter: filter,
            sort: sort,
        });
    }

    static async fetchAllTagsFromSite(): Promise<ApiResponse<SearchResponse<Tag[]>>> {
        const siteId = getCurrentSiteId();
        return await apiService.get<SearchResponse<Tag[]>>(SearchServiceEndpoints.stageTags(siteId));
    }

    static async fetchAllTagsFromSiteByType(TagDesign: string): Promise<ApiResponse<SearchResponse<Tag[]>>> {
        const siteId = getCurrentSiteId();
        return await apiService.get<SearchResponse<Tag[]>>(SearchServiceEndpoints.stageTagsByType(siteId, TagDesign));
    }

    static async searchStageCounties(siteId: string): Promise<ApiResponse<SearchResponse<Tag[]>>> {
        return await apiService.post<SearchResponse<Tag[]>>(SearchServiceEndpoints.searchStageCounties, {
            filter: {
                siteId: [siteId],
            },
            sort: ['name:asc'],
        });
    }

    static async searchByIdsStage(
        ids: string[],
        currentSiteOnly?: boolean
    ): Promise<ApiResponse<SearchResponse<SearchResult[]>>> {
        const filters = currentSiteOnly
            ? { ids: ids, siteId: [getCurrentSiteId()] }
            : { ids: ids };

        return await apiService.post<SearchResponse<SearchResult[]>>(SearchServiceEndpoints.searchByIdsStage, {
            filter: filters,
            sort: ['sortOrder:asc'],
        });
    }

    static async searchByIdsObject(
        ids: string[],
        currentSiteOnly?: boolean
    ): Promise<ApiResponse<SearchResponse<SearchResult[]>>> {
        const filters = currentSiteOnly
            ? { ids: ids, siteId: [getCurrentSiteId()] }
            : { ids: ids };

        return await apiService.post<SearchResponse<SearchResult[]>>(SearchServiceEndpoints.searchByIdsObject, {
            filter: filters,
            sort: ['sortOrder:asc'],
        });
    }

    static async getStageObjects(options: {
        siteId: string;
        stageIds: string[];
        hideSold?: boolean;
        isEditMode?: boolean;
        user?: User | null;
    }): Promise<SearchResult[]> {
        const { siteId, stageIds, hideSold = false, isEditMode = false, user } = options;
        const statuses = [
            ObjectStatus.FOR_SALE,
            ObjectStatus.BOOKED,
            ObjectStatus.RESERVED,
            ObjectStatus.PRESELLING,
            ObjectStatus.SHOWING_APARTMENT,
        ];

        if (!hideSold) {
            statuses.push(ObjectStatus.SOLD);
        }

        try {
            let filter: ApiFilters = {
                siteId: [siteId],
                stageId: stageIds,
                objectStatus: statuses,
            };

            if (isEditMode) {
                filter = { ...filter, isEditMode: true };
            }

            if (SearchService.hasMadePurchase(stageIds, user)) {
                filter = { ...filter, hasMadePurchase: true };
            }

            const response = await SearchService.fetchObjects(filter, SortBy.ACCESS_ASC, 0, 200);
            if (response.error) throw apiErrorFrom(response);
            return response.data!.hits;
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    private static hasMadePurchase(stageIds: string[], user?: User | null): boolean {
        if (user && user.purchases.some((x) => stageIds.includes(x.stageId))) return true;
        return false;
    }
}
